import { useState, useEffect } from 'react';
import { FiBook, FiShoppingCart, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { apiGetDashboardStats } from '../api';
import LoadError from '../components/LoadError';

const StatCard = ({ icon: Icon, label, value, color, trend, detail }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4 hover:shadow-md transition">
    <span className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
      <Icon size={22} className="text-white" />
    </span>
    <div className="flex-1">
      <p className="text-2xl font-bold text-black">{value}</p>
      <p className="text-sm text-black">{label}</p>
      {detail && <p className="mt-1 text-[11px] text-gray-600">{detail}</p>}
    </div>
    {trend && (
      <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
        <FiTrendingUp size={12} /> {trend}
      </span>
    )}
  </div>
);

const formatMoney = (amount) => new Intl.NumberFormat(
  'en-IN',
  { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }
).format(Number(amount) || 0);

const Dashboard = () => {
  const [stats, setStats] = useState({ books: 0, orders: 0, users: 0, revenue: 0 });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [revenueDays, setRevenueDays] = useState('30');

  const loadDashboard = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await apiGetDashboardStats(revenueDays);
        setStats(data.stats || { books: 0, orders: 0, users: 0, revenue: 0 });
        setRecentOrders(data.recentOrders || []);
      } catch (err) {
        setError(err.message || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

  useEffect(() => {
    loadDashboard();
    // Dashboard data is intentionally loaded once on mount; Retry calls the
    // same function explicitly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revenueDays]);

  if (loading) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full mx-auto"></div>
        <p className="text-black mt-4">Loading dashboard...</p>
      </div>
    );
  }

  if (error) return <LoadError message={error} onRetry={loadDashboard} />;

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          Revenue period
          <select value={revenueDays} onChange={(e) => setRevenueDays(e.target.value)} className="rounded-lg border border-gray-300 bg-white px-3 py-2">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 365 days</option>
          </select>
        </label>
      </div>
      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={FiBook} label="Total Books" value={stats.books} color="bg-blue-500" />
        <StatCard
          icon={FiShoppingCart}
          label="Total Orders"
          value={stats.orders}
          color="bg-orange-500"
        />
        <StatCard icon={FiUsers} label="Total Users" value={stats.users} color="bg-green-500" />
        <StatCard
          icon={FiTrendingUp}
          label="Net Revenue (INR)"
          value={formatMoney(stats.revenue)}
          color="bg-purple-500"
          detail={`Gross ${formatMoney(stats.grossRevenue)} · Refunds ${formatMoney(stats.refunds)}`}
        />
      </div>

      {/* Recent Orders */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="font-semibold text-black">Recent Orders</h2>
          <a href="/orders" className="text-sm text-orange-600 hover:underline">
            View All
          </a>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-black">
              <tr>
                <th className="px-5 py-3 text-left">Order ID</th>
                <th className="px-5 py-3 text-left">Customer</th>
                <th className="px-5 py-3 text-left">Items</th>
                <th className="px-5 py-3 text-left">Total</th>
                <th className="px-5 py-3 text-left">Status</th>
                <th className="px-5 py-3 text-left">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-5 py-3 font-medium text-black">
                    #{order.orderId || order.id}
                  </td>
                  <td className="px-5 py-3">{order.user?.name || 'N/A'}</td>
                  <td className="px-5 py-3">{order.itemCount ?? '—'}</td>
                  <td className="px-5 py-3 font-medium">
                    {formatMoney(order.displayAmountINR)}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.status === 'delivered'
                          ? 'bg-green-100 text-green-700'
                          : order.status === 'cancelled'
                            ? 'bg-red-100 text-red-700'
                            : order.status === 'shipped'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-yellow-100 text-yellow-700'
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-black">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </td>
                </tr>
              ))}
              {recentOrders.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-black">
                    No orders yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
